import React, { useState, useEffect } from 'react';
import { clickable } from '../../../utils/a11y';
import { Link, useNavigate } from 'react-router-dom';
import './CloudStaffDashboard.css';
import './CompanyGeneration.css';
import cloudsafeLogo from '../../../assets/cloudsafe-logo.png';
import CloudStaffSidebar from './CloudStaffSidebar';
import {
  FaArrowLeft, FaBuilding, FaUserCircle, FaSignOutAlt, FaBell,
  FaPlus, FaTimes, FaBriefcase, FaHardHat, FaEdit, FaBan, FaTrashAlt, FaBars
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getUserRole } from '../../../utils/roleUtils';
import { clearToken } from '../../../utils/auth';
import { getApiEndpoint } from '../../../config/api';

const AREA_OPTIONS = [
  { id: 'overview', label: 'Overview' },
  { id: 'fire', label: 'Fire Safety' },
  { id: 'roof', label: 'Roof Safety' },
  { id: 'staff', label: 'Staff Safety' },
  { id: 'equipment', label: 'Equipment Safety' },
  { id: 'electrical', label: 'Electrical Safety' },
  { id: 'building', label: 'Building Maintenance' },
  { id: 'emergency', label: 'Emergency Preparedness' },
  { id: 'management', label: 'View Management' },
  { id: 'profile', label: 'Property Profile' },
  { id: 'settings', label: 'Settings' }
];

const CompanyGeneration = () => {
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [userName, setUserName] = useState('');
  const [fmCompanies, setFmCompanies] = useState([]);
  const [contractorCompanies, setContractorCompanies] = useState([]);
  const [eligibleUsers, setEligibleUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    type: 'fm',
    name: '',
    primary_user_id: '',
    allowed_sections: []
  });
  const [userIdInput, setUserIdInput] = useState('');
  const [lookedUpUser, setLookedUpUser] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [detailModal, setDetailModal] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [userDetailLoading, setUserDetailLoading] = useState(false);
  const [editCompany, setEditCompany] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', primary_user_id: '', allowed_sections: [] });
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [actionSubmitting, setActionSubmitting] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  useEffect(() => {
    const userRole = getUserRole();
    if (userRole !== 'cloud_staff' && userRole !== 'super_admin') {
      toast.error('Access denied');
      navigate('/login');
      return;
    }
    loadProfile();
    loadCompanies();
  }, [navigate]);

  useEffect(() => {
    if (form.type && showCreate) {
      loadEligibleUsers(form.type);
    } else {
      setEligibleUsers([]);
      setForm(prev => ({ ...prev, primary_user_id: '' }));
    }
    setLookedUpUser(null);
    setUserIdInput('');
  }, [form.type, showCreate]);

  useEffect(() => {
    if (editCompany) loadEligibleUsers(editCompany.type);
  }, [editCompany]);

  const loadProfile = async () => {
    try {
      const res = await fetch(getApiEndpoint('/api/users/profile'), {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const d = await res.json();
        setUserName(`${d.firstName || d.first_name || ''} ${d.lastName || d.last_name || ''}`);
      }
    } catch (e) {}
  };

  const loadCompanies = async () => {
    setLoading(true);
    try {
      const res = await fetch(getApiEndpoint('/api/cloud-staff/companies'), {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setFmCompanies(data.fm_companies || []);
        setContractorCompanies(data.contractor_companies || []);
      } else if (res.status === 404) {
        toast.warning('Company Generation API not found (404). Deploy the latest backend and restart the server, then refresh this page.');
        setFmCompanies([]);
        setContractorCompanies([]);
      } else {
        toast.error(res.status === 401 ? 'Please sign in again' : 'Failed to load companies');
      }
    } catch (e) {
      toast.error('Failed to load companies. Check the API URL and that the backend is running.');
    } finally {
      setLoading(false);
    }
  };

  const loadEligibleUsers = async (type) => {
    try {
      const res = await fetch(
        getApiEndpoint(`/api/cloud-staff/companies/users-eligible?type=${type}`),
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setEligibleUsers(Array.isArray(data) ? data : []);
      } else {
        if (res.status === 404) toast.warning('Company API not found. Deploy the latest backend.');
        setEligibleUsers([]);
      }
    } catch (e) {
      setEligibleUsers([]);
    }
  };

  const handleLookupByUserId = async () => {
    const id = String(userIdInput).trim();
    if (!id) { toast.warning('Enter a user ID'); return; }
    setLookupLoading(true);
    setLookedUpUser(null);
    try {
      const url = getApiEndpoint(`/api/cloud-staff/companies/user-lookup?id=${encodeURIComponent(id)}`);
      const res = await fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      const data = await res.json();
      if (res.ok) {
        setLookedUpUser(data);
        setForm(prev => ({ ...prev, primary_user_id: String(data.id) }));
        if (data.current_company_name) {
          toast.info(`${data.first_name} ${data.last_name} is already primary for "${data.current_company_name}". They can only be primary for one company.`);
        } else {
          toast.success(`Found: ${data.first_name} ${data.last_name}`);
        }
      } else {
        toast.error(data.message || 'User not found');
      }
    } catch (e) {
      toast.error('Lookup failed. Check network and try again.');
    } finally {
      setLookupLoading(false);
    }
  };

  const handleCreateCompany = async () => {
    if (!form.name?.trim()) { toast.warning('Company name is required'); return; }
    if (!form.primary_user_id) { toast.warning('Please select the primary user (main contact)'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(getApiEndpoint('/api/cloud-staff/companies'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type: form.type,
          name: form.name.trim(),
          primary_user_id: parseInt(form.primary_user_id, 10),
          allowed_sections: Array.isArray(form.allowed_sections) ? form.allowed_sections : []
        })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || 'Company created');
        setForm({ type: 'fm', name: '', primary_user_id: '', allowed_sections: [] });
        setShowCreate(false);
        loadCompanies();
        loadEligibleUsers(form.type === 'fm' ? 'fm' : 'contractor');
      } else {
        toast.error(data.message || 'Failed to create company');
      }
    } catch (e) {
      toast.error('Failed to create company');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    clearToken();
    localStorage.removeItem('userRole');
    localStorage.removeItem('token');
    navigate('/login');
  };

  const primaryDisplay = (row) => {
    if (row.primary_first_name || row.primary_last_name || row.primary_email) {
      return `${row.primary_first_name || ''} ${row.primary_last_name || ''}`.trim() || row.primary_email || '—';
    }
    return '—';
  };

  const fetchUserDetail = async (userId) => {
    if (!userId) return;
    setUserDetailLoading(true);
    setUserDetail(null);
    try {
      const res = await fetch(getApiEndpoint(`/api/cloud-staff/companies/user-lookup/${userId}`), {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) setUserDetail(await res.json());
      else toast.error('Could not load user details');
    } catch (e) {
      toast.error('Failed to load user');
    } finally {
      setUserDetailLoading(false);
    }
  };

  const openCompanyDetail = (row) => setDetailModal({ type: 'company', data: row });
  const openUserDetail = (row) => {
    if (!row.primary_user_id) return;
    setDetailModal({ type: 'user', data: row });
    fetchUserDetail(row.primary_user_id);
  };
  const closeDetailModal = () => { setDetailModal(null); setUserDetail(null); };

  const openEdit = (row, type) => {
    setEditCompany({ type, row });
    setEditForm({
      name: row.name || '',
      primary_user_id: row.primary_user_id ? String(row.primary_user_id) : '',
      allowed_sections: Array.isArray(row.allowed_sections) ? [...row.allowed_sections] : []
    });
    loadEligibleUsers(type);
  };
  const closeEdit = () => {
    setEditCompany(null);
    setEditForm({ name: '', primary_user_id: '', allowed_sections: [] });
    setConfirmAction(null);
  };

  const toggleSection = (sectionId, isCreate) => {
    if (isCreate) {
      setForm(prev => ({
        ...prev,
        allowed_sections: prev.allowed_sections.includes(sectionId)
          ? prev.allowed_sections.filter(s => s !== sectionId)
          : [...prev.allowed_sections, sectionId]
      }));
    } else {
      setEditForm(prev => ({
        ...prev,
        allowed_sections: prev.allowed_sections.includes(sectionId)
          ? prev.allowed_sections.filter(s => s !== sectionId)
          : [...prev.allowed_sections, sectionId]
      }));
    }
  };

  const handleSuspend = async () => {
    if (!editCompany) return;
    setActionSubmitting(true);
    try {
      const res = await fetch(getApiEndpoint(`/api/cloud-staff/companies/${editCompany.type}/${editCompany.row.id}/suspend`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      if (res.ok) { toast.success(data.message || 'Company suspended'); setConfirmAction(null); closeEdit(); loadCompanies(); }
      else toast.error(data.message || 'Failed to suspend');
    } catch (e) { toast.error('Failed to suspend company'); }
    finally { setActionSubmitting(false); }
  };

  const handleUnsuspend = async () => {
    if (!editCompany) return;
    setActionSubmitting(true);
    try {
      const res = await fetch(getApiEndpoint(`/api/cloud-staff/companies/${editCompany.type}/${editCompany.row.id}/unsuspend`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      if (res.ok) { toast.success(data.message || 'Company unsuspended'); setConfirmAction(null); closeEdit(); loadCompanies(); }
      else toast.error(data.message || 'Failed to unsuspend');
    } catch (e) { toast.error('Failed to unsuspend company'); }
    finally { setActionSubmitting(false); }
  };

  const handleRemove = async () => {
    if (!editCompany) return;
    setActionSubmitting(true);
    try {
      const res = await fetch(getApiEndpoint(`/api/cloud-staff/companies/${editCompany.type}/${editCompany.row.id}`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      if (res.ok) { toast.success(data.message || 'Company removed'); setConfirmAction(null); closeEdit(); loadCompanies(); }
      else toast.error(data.message || 'Failed to remove company');
    } catch (e) { toast.error('Failed to remove company'); }
    finally { setActionSubmitting(false); }
  };

  const handleSaveEdit = async () => {
    if (!editCompany) return;
    const { type, row } = editCompany;
    if (!editForm.name?.trim()) { toast.warning('Company name is required'); return; }
    setEditSubmitting(true);
    try {
      const res = await fetch(getApiEndpoint(`/api/cloud-staff/companies/${type}/${row.id}`), {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: editForm.name.trim(),
          primary_user_id: editForm.primary_user_id ? parseInt(editForm.primary_user_id, 10) : undefined,
          allowed_sections: Array.isArray(editForm.allowed_sections) ? editForm.allowed_sections : undefined
        })
      });
      const data = await res.json();
      if (res.ok) { toast.success('Company updated'); closeEdit(); loadCompanies(); }
      else toast.error(data.message || 'Update failed');
    } catch (e) { toast.error('Failed to update company'); }
    finally { setEditSubmitting(false); }
  };

  return (
    <div className="cs-dashboard cg-page">
      {/* ── Header ── */}
      <header className="cs-header">
        <div className="cs-header-left">
          <button className="cs-mobile-menu-btn" onClick={() => setMobileSidebarOpen(true)}><FaBars /></button>
          <img src={cloudsafeLogo} alt="CloudSafe" className="cs-logo" />
          <div className="cs-header-badge"><FaBuilding /><span>Cloud Staff Portal</span></div>
        </div>
        <div className="cs-header-right">
          <div className="cs-header-icons">
            <button className="cs-icon-btn" title="Notifications"><FaBell /></button>
            <div className="cs-user-avatar" aria-haspopup="menu" aria-expanded={showUserMenu}
                 {...clickable(() => setShowUserMenu(!showUserMenu), { label: 'User menu' })}>
              <FaUserCircle />
              {showUserMenu && (
                <div className="cs-user-menu">
                  <div className="cs-user-menu-header">
                    <FaUserCircle className="cs-menu-avatar" />
                    <div>
                      <p className="cs-menu-name">{userName || 'Cloud Staff'}</p>
                      <p className="cs-menu-role">Cloud Staff Admin</p>
                    </div>
                  </div>
                  <div className="cs-user-menu-divider" />
                  <button onClick={handleLogout} className="cs-menu-item cs-menu-logout">
                    <FaSignOutAlt /> Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="cs-layout">
        <CloudStaffSidebar active="company-generation" mobileOpen={mobileSidebarOpen} onClose={() => setMobileSidebarOpen(false)} />
        <main className="cs-content">

          {/* ── Page Title Bar ── */}
          <div className="cg-topbar">
            <div className="cg-topbar-left">
              <Link to="/cloud-staff/dashboard" className="act-back-btn"><FaArrowLeft /></Link>
              <div>
                <h1 className="cg-title">
                  <FaBuilding className="cg-title-icon" /> Company Generation
                </h1>
                <p className="cg-subtitle">
                  Create FM or Contractor companies and assign the main contact user. The 6-digit CloudSafe Company ID is generated here.
                </p>
              </div>
            </div>
            <button onClick={() => setShowCreate(!showCreate)} className="cs-primary-btn">
              <FaPlus /> New Company
            </button>
          </div>

          {/* ── Create Company Form ── */}
          {showCreate && (
            <div className="cg-form-panel">
              <div className="cg-form-panel-header">
                <h3 className="cg-form-panel-title">Create company</h3>
                <button onClick={() => setShowCreate(false)} className="cg-close-btn">
                  <FaTimes />
                </button>
              </div>

              <div className="cg-form-grid">
                <div>
                  <label className="cg-label">Company type</label>
                  <select
                    className="cg-select"
                    value={form.type}
                    onChange={(e) => setForm(prev => ({ ...prev, type: e.target.value, primary_user_id: '' }))}
                  >
                    <option value="fm">FM (Management company)</option>
                    <option value="contractor">Contractor company</option>
                  </select>
                </div>

                <div>
                  <label className="cg-label">Company name</label>
                  <input
                    type="text"
                    className="cg-input"
                    placeholder="e.g. Acme Facilities Ltd"
                    value={form.name}
                    onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                  />
                </div>

                <div className="cg-full">
                  <label className="cg-label">Areas this company can access</label>
                  <p className="cg-hint">
                    When this company has access to a building, users will only see these sections in the property menu. Leave all unchecked for full access.
                  </p>
                  <div className="cg-checkbox-grid">
                    {AREA_OPTIONS.map((opt) => (
                      <label key={opt.id} className="cg-checkbox-label">
                        <input
                          type="checkbox"
                          checked={form.allowed_sections.includes(opt.id)}
                          onChange={() => toggleSection(opt.id, true)}
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="cg-full">
                  <label className="cg-label">Primary user (main contact for this company)</label>
                  <select
                    className="cg-select"
                    value={form.primary_user_id}
                    onChange={(e) => {
                      setForm(prev => ({ ...prev, primary_user_id: e.target.value }));
                      setLookedUpUser(null);
                      setUserIdInput('');
                    }}
                  >
                    <option value="">— Select user —</option>
                    {eligibleUsers.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.first_name} {u.last_name} ({u.email})
                        {u.current_company_name ? ` — Already primary for ${u.current_company_name}` : ''}
                      </option>
                    ))}
                  </select>
                  <p className="cg-hint">
                    {form.type === 'fm'
                      ? <>Listed: users with role <strong>staff</strong>, <strong>fm</strong>, <strong>admin</strong>, <strong>account_type fm</strong>, or already linked to an FM company.</>
                      : <>Listed: users with role <strong>contractor</strong>, <strong>account_type contractor_admin/contractor_staff</strong>, or already linked to a contractor company.</>
                    }
                  </p>

                  <div className="cg-lookup-row">
                    <span className="cg-lookup-label">Or find by User ID:</span>
                    <input
                      type="number"
                      className="cg-lookup-input"
                      placeholder="e.g. 42"
                      value={userIdInput}
                      onChange={(e) => setUserIdInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleLookupByUserId())}
                    />
                    <button
                      type="button"
                      className="cg-lookup-btn"
                      onClick={handleLookupByUserId}
                      disabled={lookupLoading}
                    >
                      {lookupLoading ? 'Finding…' : 'Find'}
                    </button>
                  </div>

                  {lookedUpUser && (
                    <div className="cg-lookup-result">
                      <strong>Found: </strong>
                      {lookedUpUser.first_name} {lookedUpUser.last_name} (ID {lookedUpUser.id}) — {lookedUpUser.email}
                      {lookedUpUser.current_company_name && (
                        <span className="cg-lookup-warning">
                          Already primary for: {lookedUpUser.current_company_name}. Creating a new company with this user will fail unless they are not yet linked.
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="cg-form-footer">
                <button onClick={handleCreateCompany} className="cg-btn-primary" disabled={submitting}>
                  {submitting ? 'Creating…' : 'Create company'}
                </button>
              </div>
            </div>
          )}

          {/* ── Companies List ── */}
          {loading ? (
            <div className="cs-loading"><div className="cs-spinner" /><p className="cg-loading-text">Loading companies...</p></div>
          ) : (
            <>
              {/* FM Companies */}
              <div className="cs-section" style={{ marginBottom: '2rem' }}>
                <h2 className="cg-section-title">
                  <FaBriefcase style={{ color: '#34d399', marginRight: '0.4rem' }} /> FM Companies
                </h2>
                {fmCompanies.length === 0 ? (
                  <p className="cg-empty">No FM companies yet. Create one above.</p>
                ) : (
                  <div className="cg-table-wrap">
                    <table className="cg-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>CloudSafe ID</th>
                          <th>Primary Contact</th>
                          <th>Created</th>
                          <th style={{ width: 90 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {fmCompanies.map((row) => (
                          <tr key={`fm-${row.id}`}>
                            <td>
                              <button type="button" onClick={() => openCompanyDetail(row)} className="cg-name-btn">
                                {row.name}
                              </button>
                            </td>
                            <td><span className="cg-code-badge">{row.company_code}</span></td>
                            <td>
                              {row.primary_user_id
                                ? <button type="button" onClick={() => openUserDetail(row)} className="cg-contact-btn">{primaryDisplay(row)}</button>
                                : <span className="cg-dash">—</span>
                              }
                            </td>
                            <td><span className="cg-date">{row.created_at ? new Date(row.created_at).toLocaleDateString() : '—'}</span></td>
                            <td>
                              <button type="button" onClick={() => openEdit(row, 'fm')} className="cg-edit-btn">
                                <FaEdit /> Edit
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Contractor Companies */}
              <div className="cs-section">
                <h2 className="cg-section-title">
                  <FaHardHat style={{ color: '#fb923c', marginRight: '0.4rem' }} /> Contractor Companies
                </h2>
                {contractorCompanies.length === 0 ? (
                  <p className="cg-empty">No contractor companies yet. Create one above.</p>
                ) : (
                  <div className="cg-table-wrap">
                    <table className="cg-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>CloudSafe ID</th>
                          <th>Primary Contact</th>
                          <th>Created</th>
                          <th style={{ width: 90 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {contractorCompanies.map((row) => (
                          <tr key={`cc-${row.id}`}>
                            <td>
                              <button type="button" onClick={() => openCompanyDetail(row)} className="cg-name-btn">
                                {row.name}
                              </button>
                            </td>
                            <td><span className="cg-code-badge">{row.company_code}</span></td>
                            <td>
                              {row.primary_user_id
                                ? <button type="button" onClick={() => openUserDetail(row)} className="cg-contact-btn">{primaryDisplay(row)}</button>
                                : <span className="cg-dash">—</span>
                              }
                            </td>
                            <td><span className="cg-date">{row.created_at ? new Date(row.created_at).toLocaleDateString() : '—'}</span></td>
                            <td>
                              <button type="button" onClick={() => openEdit(row, 'contractor')} className="cg-edit-btn">
                                <FaEdit /> Edit
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ── Detail Modal ── */}
          {detailModal && (
            <div className="cg-overlay" onClick={closeDetailModal}>
              <div className="cg-modal cg-modal-sm" onClick={e => e.stopPropagation()}>
                <div className="cg-modal-header">
                  <h3 className="cg-modal-title">
                    {detailModal.type === 'company'
                      ? <><FaBriefcase className="cg-modal-title-icon" /> Company Details</>
                      : <><FaUserCircle className="cg-modal-title-icon" /> Contact Details</>
                    }
                  </h3>
                  <button type="button" onClick={closeDetailModal} className="cg-close-btn"><FaTimes /></button>
                </div>
                <div className="cg-modal-body">
                  {detailModal.type === 'company' && (
                    <dl className="cg-detail-list">
                      <dt>Name</dt>
                      <dd>{detailModal.data.name}</dd>
                      <dt>CloudSafe ID (6-digit)</dt>
                      <dd><span className="cg-code-badge">{detailModal.data.company_code}</span></dd>
                      <dt>Primary Contact</dt>
                      <dd>{primaryDisplay(detailModal.data)}</dd>
                      <dt>Created</dt>
                      <dd className="cg-dd-dim">{detailModal.data.created_at ? new Date(detailModal.data.created_at).toLocaleDateString() : '—'}</dd>
                    </dl>
                  )}
                  {detailModal.type === 'user' && (
                    <>
                      {userDetailLoading && <p className="cg-loading-text">Loading…</p>}
                      {!userDetailLoading && userDetail && (
                        <dl className="cg-detail-list">
                          <dt>Name</dt>
                          <dd>{userDetail.first_name} {userDetail.last_name}</dd>
                          <dt>Email</dt>
                          <dd>{userDetail.email}</dd>
                          <dt>User ID</dt>
                          <dd className="cg-dd-dim">{userDetail.id}</dd>
                          <dt>Role</dt>
                          <dd className="cg-dd-dim">{userDetail.role || '—'}</dd>
                          <dt>Account Type</dt>
                          <dd className="cg-dd-dim">{userDetail.account_type || '—'}</dd>
                          {userDetail.current_company_name && (
                            <>
                              <dt>Current Company</dt>
                              <dd className="cg-dd-warn">{userDetail.current_company_name}</dd>
                            </>
                          )}
                        </dl>
                      )}
                      {!userDetailLoading && !userDetail && <p className="cg-loading-text">No details available.</p>}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── Edit Company Modal ── */}
          {editCompany && (
            <div className="cg-overlay" onClick={closeEdit}>
              <div className="cg-modal cg-modal-md" onClick={e => e.stopPropagation()}>
                <div className="cg-modal-header">
                  <h3 className="cg-modal-title">
                    <FaEdit className="cg-modal-title-icon" /> Edit Company
                  </h3>
                  <button type="button" onClick={closeEdit} className="cg-close-btn"><FaTimes /></button>
                </div>
                <div className="cg-modal-body">
                  <div className="cg-field">
                    <label className="cg-label">Company name</label>
                    <input
                      type="text"
                      className="cg-input"
                      value={editForm.name}
                      onChange={e => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                    />
                  </div>

                  <div className="cg-field">
                    <label className="cg-label">Primary contact</label>
                    <select
                      className="cg-select"
                      value={editForm.primary_user_id}
                      onChange={e => setEditForm(prev => ({ ...prev, primary_user_id: e.target.value }))}
                    >
                      <option value="">— No change —</option>
                      {eligibleUsers.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.first_name} {u.last_name} ({u.email})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="cg-field">
                    <label className="cg-label">Areas this company can access</label>
                    <p className="cg-hint">Only these sections will show in the property menu for this company. Leave all unchecked for full access.</p>
                    <div className="cg-checkbox-grid">
                      {AREA_OPTIONS.map((opt) => (
                        <label key={opt.id} className="cg-checkbox-label">
                          <input
                            type="checkbox"
                            checked={editForm.allowed_sections.includes(opt.id)}
                            onChange={() => toggleSection(opt.id, false)}
                          />
                          {opt.label}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="cg-danger-zone">
                    <div className="cg-danger-label">Company Actions</div>
                    <div className="cg-action-btns">
                      {editCompany.row.is_active ? (
                        <button type="button" onClick={() => setConfirmAction('suspend')} disabled={actionSubmitting} className="cg-btn-suspend">
                          <FaBan /> Suspend
                        </button>
                      ) : (
                        <button type="button" onClick={() => setConfirmAction('unsuspend')} disabled={actionSubmitting} className="cg-btn-unsuspend">
                          <FaBan /> Unsuspend
                        </button>
                      )}
                      <button type="button" onClick={() => setConfirmAction('remove')} disabled={actionSubmitting} className="cg-btn-remove">
                        <FaTrashAlt /> Remove
                      </button>
                    </div>
                    <p className="cg-danger-hint">
                      Suspend blocks all logins for this company. Remove deletes the company and unlinks all users.
                    </p>
                  </div>

                  <div className="cg-modal-footer">
                    <button type="button" onClick={closeEdit} className="cg-btn-cancel">Cancel</button>
                    <button type="button" onClick={handleSaveEdit} disabled={editSubmitting} className="cg-btn-primary">
                      {editSubmitting ? 'Saving…' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Confirm Action Modal ── */}
          {editCompany && confirmAction && (
            <div className="cg-overlay" style={{ zIndex: 1001 }} onClick={() => !actionSubmitting && setConfirmAction(null)}>
              <div className="cg-modal cg-modal-sm" onClick={e => e.stopPropagation()}>
                <div className="cg-modal-body">
                  <h4 className="cg-confirm-title">
                    {confirmAction === 'suspend' && 'Suspend this company?'}
                    {confirmAction === 'unsuspend' && 'Unsuspend this company?'}
                    {confirmAction === 'remove' && 'Remove this company?'}
                  </h4>
                  <p className="cg-confirm-body">
                    {confirmAction === 'suspend' && 'All users under this company will be blocked from logging in until you unsuspend. Company admin will see: "Please contact CloudSafe for assistance." Staff will see: "Please contact your company admin."'}
                    {confirmAction === 'unsuspend' && 'Users will be able to log in again.'}
                    {confirmAction === 'remove' && 'This will permanently remove the company and unlink all users. This cannot be undone.'}
                  </p>
                  <div className="cg-modal-footer">
                    <button type="button" onClick={() => setConfirmAction(null)} disabled={actionSubmitting} className="cg-btn-cancel">Cancel</button>
                    {confirmAction === 'suspend' && (
                      <button type="button" onClick={handleSuspend} disabled={actionSubmitting} className="cg-btn-suspend">
                        {actionSubmitting ? 'Suspending…' : 'Suspend'}
                      </button>
                    )}
                    {confirmAction === 'unsuspend' && (
                      <button type="button" onClick={handleUnsuspend} disabled={actionSubmitting} className="cg-btn-unsuspend">
                        {actionSubmitting ? 'Unsuspending…' : 'Unsuspend'}
                      </button>
                    )}
                    {confirmAction === 'remove' && (
                      <button type="button" onClick={handleRemove} disabled={actionSubmitting} className="cg-btn-remove">
                        {actionSubmitting ? 'Removing…' : 'Remove'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

        </main>
      </div>
    </div>
  );
};

export default CompanyGeneration;
